import { useState } from 'react';
import { DeviceSize, DeviceSizeField } from '@frontend/device-size-field';
import { useTranslation } from '@frontend/i18n';
import { BulkEmailPrefixes } from '@frontend/tracking-prefixes';
import { theme } from '@frontend/theme';
import { BaseFieldProps, TextField, usePopoverDialog } from '@frontend/design-system';
import { COMPOSER_BREAKPOINTS } from '../../content-composer/constants';
import { ContentComposer } from '../../content-composer/content-composer';
import { AddSectionButton } from './add-section-button';
import { DynamicFieldButton } from './dynamic-field-button';
import { EmailAssistantButton } from './email-assistant-button';
import { EmailHeaderContext } from './email-header-context';
import { EmojiButton } from './emoji-button';
import { PreviewButton } from './preview-button';
import { SendTestEmailButton } from './send-test-email-button';

const SUBJECT_FIELD_NAME = 'subject';

type Props = {
  deviceSizeProps: BaseFieldProps<DeviceSize>;
  subjectProps?: BaseFieldProps<string>;
  trackingPrefix: string;
};

export const EmailHeader = ({ deviceSizeProps, subjectProps, trackingPrefix }: Props) => {
  const { t } = useTranslation('email-composer');
  const [subject, setSubject] = useState(subjectProps?.value ?? '');

  // DYNAMIC FIELD LOGIC
  const dynamicFieldPopoverDialogProps = usePopoverDialog({
    placement: 'bottom-start',
    initialOffset: {
      x: -170,
      y: 0,
    },
  });

  // EMOJI PICKER LOGIC
  const emojiPopoverDialogProps = usePopoverDialog({
    placement: 'bottom-start',
    initialOffset: {
      x: -252,
      y: 0,
    },
  });

  return (
    <EmailHeaderContext.Provider value={{ setSubject, subject, subjectProps, trackingPrefix }}>
      <ContentComposer.Header
        css={{
          columnGap: theme.spacing(1),
          gridTemplateColumns: '1fr auto auto',
          padding: theme.spacing(2),

          [`@media (max-width: ${COMPOSER_BREAKPOINTS.MOBILE}px)`]: {
            alignItems: 'unset',
            gridTemplateAreas: `'subject' 'bottom'`,
            gridTemplateColumns: 'auto',
            rowGap: theme.spacing(2),
            transition: 'display 0.3s ease-in-out',
          },
        }}
      >
        <TextField
          label={t('Subject')}
          name={SUBJECT_FIELD_NAME}
          {...(subjectProps as BaseFieldProps<string>)}
          onChange={(e) => {
            // @ts-ignore DS TextField onChange event type
            const value = e.target.value;
            setSubject(value);
            subjectProps?.onChange({ ...subjectProps, value, name: 'subject' });
          }}
          css={{
            paddingRight: theme.spacing(1),
            '.subject-end-adornment': {
              display: dynamicFieldPopoverDialogProps.isOpen || emojiPopoverDialogProps.isOpen ? 'flex' : 'none',
            },
            '&:focus-within .subject-end-adornment': {
              display: 'flex',
            },
            [`@media (max-width: ${COMPOSER_BREAKPOINTS.MOBILE}px)`]: {
              gridArea: 'subject',
            },
          }}
          endAdornment={
            <div
              className='subject-end-adornment'
              css={{
                alignItems: 'center',
                columnGap: theme.spacing(1),
                button: {
                  height: 'auto',
                },
              }}
            >
              <DynamicFieldButton {...dynamicFieldPopoverDialogProps} />
              <EmojiButton {...emojiPopoverDialogProps} />
              <EmailAssistantButton />
            </div>
          }
        />
        <DeviceSizeField {...deviceSizeProps} trackingIdPrefix={BulkEmailPrefixes.Composer} />
        <div
          css={{
            alignItems: 'center',
            columnGap: theme.spacing(1),
            display: 'flex',
            height: '100%',
            maxHeight: '40px',
            [`@media (max-width: ${COMPOSER_BREAKPOINTS.MOBILE}px)`]: {
              gridArea: 'bottom',
              justifyContent: 'space-between',
            },
          }}
        >
          <div
            css={{
              alignItems: 'center',
              columnGap: theme.spacing(1),
              display: 'flex',
              height: '100%',
            }}
          >
            <AddSectionButton />
            <PreviewButton {...deviceSizeProps} />
            <SendTestEmailButton subject={subjectProps?.value ?? ''} />
          </div>
        </div>
      </ContentComposer.Header>
    </EmailHeaderContext.Provider>
  );
};
